import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Pencil, Trash2, Users, Eye, Loader2, AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Move, UserX } from "lucide-react";
import { SquadDragBoard } from "@/components/SquadDragBoard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_TYPE_OPTIONS = [
  { value: "training", label: "Training" },
  { value: "social", label: "Social / Recreational" },
  { value: "competitive", label: "Competitive" },
];

const GENDER_OPTIONS = [
  { value: "boys", label: "Boys" },
  { value: "girls", label: "Girls" },
  { value: "mixed", label: "Mixed" },
];

export default function AcademyTeamsManagement({ user }) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deleting, setDeleting] = useState(null); // team_id
  const [moveDialog, setMoveDialog] = useState(null); // { newTeam, candidates }
  const [collapsed, setCollapsed] = useState({}); // { [ageGroupLabel]: true }
  const [boardFor, setBoardFor] = useState(null); // age group label showing the drag board

  const hasAccess = ["convenor", "captain", "academy_admin"].includes(user?.role) ||
    (Array.isArray(user?.roles) && (user.roles.includes("admin") || user.roles.includes("super_admin")));

  useEffect(() => {
    if (hasAccess) fetchTeams();
  }, [hasAccess]);// eslint-disable-line

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/academy-teams-overview`, { withCredentials: true });
      setTeams(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (form) => {
    try {
      const res = await axios.post(
        `${API}/academy/admin/academy-teams`,
        form,
        { withCredentials: true }
      );
      toast.success(`Team "${res.data.team.name}" created`);
      setCreateDialogOpen(false);
      await fetchTeams();
      // If there are suggested move candidates, open the move dialog
      const cands = res.data.suggested_move_candidates || [];
      if (cands.length > 0) {
        setMoveDialog({ newTeam: res.data.team, candidates: cands });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to create team";
      // 409 = duplicate team id. Nudge admin to use the Squad Label field.
      if (err.response?.status === 409) {
        toast.error(`${detail}. Tip: use the Squad Label field (e.g. "A", "B") to create a sibling squad in the same age group.`);
      } else {
        toast.error(detail);
      }
    }
  };

  const handleEdit = async (team_id, form) => {
    try {
      await axios.put(`${API}/academy/admin/academy-teams/${team_id}`, form, { withCredentials: true });
      toast.success("Team updated");
      setEditingTeam(null);
      await fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update team");
    }
  };

  const handleToggleBucket = async (team_id, make_bucket) => {
    try {
      await axios.post(
        `${API}/academy/admin/academy-teams/${team_id}/set-as-bucket`,
        { is_bucket: make_bucket },
        { withCredentials: true }
      );
      toast.success(
        make_bucket
          ? "Set as auto-registration bucket. New registrations will land here."
          : "Bucket flag removed."
      );
      await fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update bucket flag");
    }
  };

  const handleDelete = async (team_id, team_name) => {
    if (!window.confirm(`Delete team "${team_name}"?\n\nThis is blocked if any players or sessions are still linked.`)) return;
    setDeleting(team_id);
    try {
      await axios.delete(`${API}/academy/admin/academy-teams/${team_id}`, { withCredentials: true });
      toast.success(`Team "${team_name}" deleted`);
      await fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete team");
    } finally {
      setDeleting(null);
    }
  };

  const handleMoveSelected = async (candidatesToMove, newTeamId) => {
    let moved = 0;
    for (const c of candidatesToMove) {
      try {
        await axios.delete(
          `${API}/academy/teams/${c.current_team_id}/remove-player/${c.player_id}`,
          { withCredentials: true }
        );
        await axios.post(
          `${API}/academy/teams/${newTeamId}/add-existing-player`,
          { player_id: c.player_id },
          { withCredentials: true }
        );
        moved += 1;
      } catch (err) {
        toast.error(`Failed to move ${c.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (moved > 0) toast.success(`Moved ${moved} player(s)`);
    setMoveDialog(null);
    await fetchTeams();
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center text-slate-300">
        <p>Admin access required.</p>
      </div>
    );
  }

  // Group teams into age-group buckets (U8, U10, ...), sorted youngest first.
  const ageLabel = (raw) => {
    const num = String(raw || "").replace(/[^0-9]/g, "");
    return num ? `U${num}` : "Unassigned";
  };
  const groups = Object.values(
    teams.reduce((acc, t) => {
      const label = ageLabel(t.age_group);
      if (!acc[label]) {
        acc[label] = { label, sort: parseInt(label.replace("U", ""), 10) || 999, teams: [] };
      }
      acc[label].teams.push(t);
      return acc;
    }, {})
  )
    .sort((a, b) => a.sort - b.sort)
    .map((g) => ({
      ...g,
      teams: [...g.teams].sort((a, b) => (b.is_bucket ? 1 : 0) - (a.is_bucket ? 1 : 0) || a.name.localeCompare(b.name)),
      players: g.teams.reduce((n, t) => n + (t.player_count || 0), 0),
      uncoached: g.teams.filter((t) => !(t.coaches && t.coaches.length > 0)).length,
    }));

  const toggleGroup = (label) => setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));
  const allCollapsed = groups.length > 0 && groups.every((g) => collapsed[g.label]);

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-slate-100" data-testid="academy-teams-mgmt-page">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Panel
          </Button>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Team
          </Button>
        </div>

        <h1 className="text-3xl font-bold mb-2">Manage Academy Teams</h1>
        <p className="text-sm text-slate-400 mb-6">
          Create, rename, and remove academy teams. Player management (add/remove/move players) lives on each team&apos;s detail page.
        </p>

        {!loading && groups.length > 0 && (
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-xs text-slate-500">
              {teams.length} teams across {groups.length} age groups
              {groups.reduce((n, g) => n + g.uncoached, 0) > 0 && (
                <span className="text-amber-400" data-testid="coach-gap-summary">
                  {" · "}{groups.reduce((n, g) => n + g.uncoached, 0)} squads need a coach
                </span>
              )}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="border-slate-700 text-slate-300"
              onClick={() =>
                setCollapsed(allCollapsed ? {} : Object.fromEntries(groups.map((g) => [g.label, true])))
              }
              data-testid="toggle-all-groups-btn"
            >
              {allCollapsed ? "Expand all" : "Collapse all"}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-400" />
          </div>
        ) : groups.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800 p-12 text-center text-slate-400" data-testid="teams-table-card">
            No teams yet. Click &quot;New Team&quot; to create your first one.
          </Card>
        ) : (
          <div className="space-y-4" data-testid="teams-table-card">
            {groups.map((g) => {
              const isOpen = !collapsed[g.label];
              return (
                <Card
                  key={g.label}
                  className="bg-slate-900/50 border-slate-800 overflow-hidden"
                  data-testid={`age-group-${g.label}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.label)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-900/80 hover:bg-slate-800/70 transition-colors text-left"
                    data-testid={`age-group-toggle-${g.label}`}
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
                    )}
                    <span className="text-lg font-bold tracking-tight text-white">{g.label}</span>
                    <span className="text-xs text-slate-400">
                      {g.teams.length} {g.teams.length === 1 ? "team" : "teams"} · {g.players} players
                    </span>
                    {g.uncoached > 0 && (
                      <span
                        className="px-2 py-0.5 text-xs rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 flex items-center gap-1"
                        data-testid={`coach-gap-${g.label}`}
                        title={`${g.uncoached} squad(s) in ${g.label} have no coach assigned`}
                      >
                        <UserX className="w-3 h-3" />
                        {g.uncoached} without coach
                      </span>
                    )}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setBoardFor(boardFor === g.label ? null : g.label); setCollapsed((p) => ({ ...p, [g.label]: false })); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setBoardFor(boardFor === g.label ? null : g.label); } }}
                      className={`ml-auto px-2.5 py-1 text-xs rounded border flex items-center gap-1 transition-colors ${
                        boardFor === g.label
                          ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300"
                          : "border-slate-700 text-slate-300 hover:bg-slate-800"
                      }`}
                      data-testid={`move-players-${g.label}-btn`}
                      title="Drag players between squads in this age group"
                    >
                      <Move className="w-3 h-3" />
                      {boardFor === g.label ? "Done moving" : "Move players"}
                    </span>
                  </button>
                  {boardFor === g.label && (
                    <SquadDragBoard ageGroup={g.label} onMoved={fetchTeams} />
                  )}
                  {isOpen && (
                    <div className="divide-y divide-slate-800">
                      {g.teams.map((t) => (
                  <div key={t.id} className="p-4 flex items-center justify-between flex-wrap gap-3 hover:bg-slate-900/40" data-testid={`team-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white">{t.name}</h3>
                        <span className="px-2 py-0.5 text-xs rounded bg-slate-800 text-slate-300">{t.age_group}</span>
                        <span className="px-2 py-0.5 text-xs rounded bg-slate-800 text-slate-300">{t.gender}</span>
                        {t.is_bucket && (
                          <span
                            className="px-2 py-0.5 text-xs rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            data-testid={`bucket-badge-${t.id}`}
                            title="New registrations for this age group + gender auto-land here"
                          >
                            📥 Auto-registration bucket
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 font-mono">{t.id}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-400 flex-wrap">
                        <span><Users className="w-3 h-3 inline mr-1" />{t.player_count} players</span>
                        <span>
                          {t.coaches && t.coaches.length > 0
                            ? `Coach: ${t.coaches.map(c => c.name || c.email).join(", ")}`
                            : <span className="text-amber-400 inline-flex items-center gap-1" data-testid={`no-coach-${t.id}`}><UserX className="w-3 h-3" /> No coach assigned</span>}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/academy/teams/${t.id}`}>
                        <Button size="sm" variant="outline" className="border-slate-600" data-testid={`view-team-${t.id}-btn`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                      </Link>
                      <Button size="sm" variant="outline" className="border-slate-600" onClick={() => setEditingTeam(t)} data-testid={`edit-team-${t.id}-btn`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className={
                          t.is_bucket
                            ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                            : "border-slate-600 text-slate-300"
                        }
                        onClick={() => handleToggleBucket(t.id, !t.is_bucket)}
                        data-testid={`toggle-bucket-${t.id}-btn`}
                        title={
                          t.is_bucket
                            ? "Currently the auto-registration bucket for this age group + gender. Click to unset."
                            : "Set this team as the auto-registration bucket for its age group + gender."
                        }
                      >
                        {t.is_bucket ? "Unset bucket" : "Set as bucket"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-700 text-red-400 hover:bg-red-900/30"
                        onClick={() => handleDelete(t.id, t.name)}
                        disabled={deleting === t.id}
                        data-testid={`delete-team-${t.id}-btn`}
                      >
                        {deleting === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <TeamFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreate}
        title="Create Team"
      />

      {editingTeam && (
        <TeamFormDialog
          open={true}
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
          onSubmit={(form) => handleEdit(editingTeam.id, form)}
          title="Edit Team"
        />
      )}

      {moveDialog && (
        <SuggestedMovesDialog
          newTeam={moveDialog.newTeam}
          candidates={moveDialog.candidates}
          onClose={() => setMoveDialog(null)}
          onMove={(selected) => handleMoveSelected(selected, moveDialog.newTeam.id)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TeamFormDialog({ open, onClose, onSubmit, title, team }) {
  const [form, setForm] = useState({
    name: team?.name || "",
    age_group: team?.age_group || "",
    gender: team?.gender || "boys",
    session_type: team?.session_type || "training",
    squad_label: "",  // e.g. "A", "B" — creates sibling squads within same age_group+gender
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm({
      name: team?.name || "",
      age_group: team?.age_group || "",
      gender: team?.gender || "boys",
      session_type: team?.session_type || "training",
      squad_label: "",  // only for CREATE — edit doesn't renamed team id
    });
  }, [team]);

  // Slug helpers matching backend _slugify_team_id (see academy_routes.py).
  const slugAgeGroup = (raw) => {
    const num = String(raw || "").replace(/[Uu]/g, "").trim();
    return num ? `u${num}` : "";
  };

  const derivedTeamId = (() => {
    if (team) return null; // edit mode
    const ag = slugAgeGroup(form.age_group);
    const gender = (form.gender || "").toLowerCase();
    if (!ag || !gender) return "";
    const suffix = form.squad_label
      ? `_${form.squad_label.toLowerCase().replace(/[^a-z0-9]/g, "")}`
      : "";
    return `${ag}${suffix}_${gender}`;
  })();

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.age_group.trim() || !form.gender) {
      toast.error("Name, age group, and gender required");
      return;
    }
    setSubmitting(true);
    try {
      // Only send `id` on create when a squad label was set — otherwise let
      // the backend derive the default id (u10_boys style).
      const payload = { ...form };
      delete payload.squad_label;
      if (!team && form.squad_label) {
        payload.id = derivedTeamId;
      }
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 text-slate-100 border-slate-800" data-testid="team-form-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Players are managed on the team&apos;s detail page (click View on the row).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Team Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. U6 Boys"
              className="bg-slate-800 border-slate-700"
              data-testid="team-name-input"
            />
          </div>
          <div>
            <Label>Age Group</Label>
            <Input
              value={form.age_group}
              onChange={(e) => setForm({ ...form, age_group: e.target.value })}
              placeholder="e.g. U6 (or just 6)"
              className="bg-slate-800 border-slate-700"
              data-testid="team-age-input"
            />
            <p className="text-xs text-slate-500 mt-1">Use &quot;U&quot; + number, e.g. U6, U11, U16. Range: U4–U24.</p>
          </div>

          {!team && (
            <div>
              <Label>Squad Label <span className="text-slate-500 text-xs">(optional — for sibling squads)</span></Label>
              <Input
                value={form.squad_label}
                onChange={(e) => setForm({ ...form, squad_label: e.target.value })}
                placeholder="e.g. Blue, Yellow, Green, A, B — leave empty for a single squad"
                className="bg-slate-800 border-slate-700"
                data-testid="team-squad-label-input"
                maxLength={12}
              />
              <p className="text-xs text-slate-500 mt-1">
                Use this to create <strong>U10 Blue Boys</strong> alongside <strong>U10 Yellow Boys</strong>. Team ID becomes{" "}
                <code className="text-amber-300">{derivedTeamId || "u{age}_{gender}"}</code>.
              </p>
            </div>
          )}
          <div>
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="team-gender-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Default Session Type</Label>
            <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="team-session-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SESSION_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="team-form-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {team ? "Save Changes" : "Create Team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

function SuggestedMovesDialog({ newTeam, candidates, onClose, onMove }) {
  const [selected, setSelected] = useState(new Set(candidates.map(c => c.player_id)));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectedList = candidates.filter(c => selected.has(c.player_id));

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 text-slate-100 border-slate-800 max-w-2xl" data-testid="suggested-moves-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Suggested Player Moves
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {candidates.length} player(s) currently on other teams match <span className="text-emerald-400 font-medium">{newTeam.name}</span> by birth year.
            Untick anyone you want to leave on their current team.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-2">
          {candidates.map(c => (
            <label
              key={c.player_id}
              className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800"
              data-testid={`move-candidate-${c.player_id}`}
            >
              <input
                type="checkbox"
                checked={selected.has(c.player_id)}
                onChange={() => toggle(c.player_id)}
                className="w-4 h-4"
              />
              <div className="flex-1">
                <p className="font-medium text-white">{c.name}</p>
                <p className="text-xs text-slate-400">
                  {c.membership_number} • born {c.birthdate} • currently on <span className="text-amber-400">{c.current_team_id}</span> → {newTeam.name}
                </p>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="skip-moves-btn">Skip for now</Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => onMove(selectedList)}
            disabled={selectedList.length === 0}
            data-testid="apply-moves-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Move {selectedList.length} Selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
